import React, { useContext, useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import { IntlContext } from "context";
import { ImagePlus, Trash2 } from "lucide-react";
import Image from "components/File/Image";
import { cn } from "lib/utils";
import type { BlobFile } from "ui/Form/Element/InputFile";
import { ACCEPTED_IMAGE_MIMES, UPLOAD_MAX_MEGABYTES, isFileTooLarge } from "helpers/file/uploadConstraints";
import { profileFieldGroupProps, profileLabelProps } from "./formStyles";
import Label from "ui/Form/Element/Label";
import FieldGroup from "ui/Form/Element/FieldGroup";
import Feedback from "ui/Form/Element/Feedback";

interface Props {
    disabled?: boolean;
    existingPictureId?: string | null;
}

const AdPhotoField: React.FC<Props> = ({ disabled = false, existingPictureId }) => {
    const intl = useContext(IntlContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <Field
            name="photo"
            validate={(value?: BlobFile | null) =>
                value && value.content && isFileTooLarge(value)
                    ? intl.formatMessage("generic.upload.too_large", { max: UPLOAD_MAX_MEGABYTES })
                    : undefined
            }
        >
            {({ input, meta }) => {
                const error = Boolean(meta.error || meta.submitError);
                const fileValue = input.value as BlobFile | null | undefined;
                const hasNewFile = Boolean(fileValue?.content);
                const showExisting = !hasNewFile && Boolean(existingPictureId);

                const clearPhoto = () => {
                    if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                    }
                    if (inputRef.current) {
                        inputRef.current.value = "";
                    }
                    input.onChange(null);
                };

                const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.target.files?.item(0);
                    if (!file) {
                        clearPhoto();
                        return;
                    }

                    if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                    }

                    const blobFile: BlobFile = {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        content: file,
                    };

                    setPreviewUrl(URL.createObjectURL(file));
                    input.onChange(blobFile);
                };

                return (
                    <FieldGroup error={error} {...profileFieldGroupProps()}>
                        <Label
                            htmlFor="ad-photo-upload"
                            className={profileLabelProps(intl.formatMessage("ad.label.photos")).className}
                        >
                            {intl.formatMessage("ad.label.photos")}
                        </Label>

                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                            {(hasNewFile && previewUrl) || showExisting ? (
                                <div className="relative h-36 w-36 shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-slate-100">
                                    {hasNewFile && previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt=""
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        existingPictureId && (
                                            <Image id={existingPictureId} className="h-full w-full object-cover" />
                                        )
                                    )}
                                    {!disabled && (
                                        <button
                                            type="button"
                                            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-slate-600 shadow-sm transition-colors hover:bg-white hover:text-destructive"
                                            onClick={clearPhoto}
                                            aria-label={intl.formatMessage("generic.action.upload.remove")}
                                        >
                                            <Trash2 className="h-4 w-4" strokeWidth={2} />
                                        </button>
                                    )}
                                </div>
                            ) : null}

                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => inputRef.current?.click()}
                                    className={cn(
                                        "flex min-h-[144px] flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 bg-slate-50 px-6 py-8 text-center transition-colors hover:border-primary/40 hover:bg-primary/5",
                                        error && "border-destructive/50"
                                    )}
                                >
                                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
                                        <ImagePlus className="h-6 w-6 text-primary" strokeWidth={2} />
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900">
                                        Ajouter une photo
                                    </span>
                                    <span className="text-xs text-slate-500">PNG ou JPEG, 10 Mo max.</span>
                                </button>
                            )}
                        </div>

                        <input
                            ref={inputRef}
                            id="ad-photo-upload"
                            type="file"
                            accept={ACCEPTED_IMAGE_MIMES}
                            className="sr-only"
                            disabled={disabled}
                            onChange={handleFileChange}
                            onBlur={input.onBlur}
                        />

                        {error && (
                            <Feedback className="field__feedback error">
                                {meta.error || meta.submitError}
                            </Feedback>
                        )}
                    </FieldGroup>
                );
            }}
        </Field>
    );
};

export default AdPhotoField;
